import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowDownRight, ArrowUpRight, Megaphone, Wallet } from "lucide-react";
import { apiGet } from "@/lib/apiAdmin";
import { MoneyTrend } from "./MoneyTrend";
import { PriceCoach } from "./PriceCoach";
import { SavingsGoals } from "./SavingsGoals";

const fmt = (n) => `$${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
const PERIODS = [["today", "Today"], ["this week", "This week"], ["this month", "This month"]];

const Period = ({ label, v, i }) => (
  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
    className="rounded-xl border border-white/10 bg-white/[0.02] p-5 space-y-3" data-testid={`money-${label.toLowerCase().replace(" ", "-")}`}>
    <p className="text-xs uppercase tracking-widest text-gray-500">{label}</p>
    <div className="grid grid-cols-3 gap-2">
      <div><p className="text-[11px] text-gray-500 flex items-center gap-1"><ArrowUpRight className="w-3 h-3 text-emerald-400" />Made</p>
        <p className="text-lg sm:text-2xl font-['Outfit'] text-emerald-400" data-testid="money-made">{fmt(v.made)}</p></div>
      <div><p className="text-[11px] text-gray-500 flex items-center gap-1"><ArrowDownRight className="w-3 h-3 text-rose-400" />Spent</p>
        <p className="text-lg sm:text-2xl font-['Outfit'] text-rose-300" data-testid="money-spent">{fmt(v.spent)}</p></div>
      <div><p className="text-[11px] text-gray-500 flex items-center gap-1"><Wallet className="w-3 h-3 text-sky-400" />Profit</p>
        <p className={`text-lg sm:text-2xl font-['Outfit'] ${v.profit < 0 ? "text-rose-400" : "text-white"}`} data-testid="money-profit">{fmt(v.profit)}</p></div>
    </div>
    <p className="text-[11px] text-gray-500">{v.payments} payment{v.payments === 1 ? "" : "s"} in the app{v.from_texts ? ` + ${fmt(v.from_texts)} from texts` : ""}{v.bank_expenses ? ` · bank ${fmt(v.bank_expenses)}` : ""}{v.refunds ? ` · refunds ${fmt(v.refunds)}` : ""}</p>
  </motion.div>
);

const WinBacks = ({ w }) => (
  <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5 space-y-3" data-testid="winback-tracker">
    <div className="flex flex-wrap items-center gap-3">
      <Megaphone className="w-4 h-4 text-sky-400" />
      <p className="text-white font-['Outfit'] flex-1">Win-back tracker</p>
      <p className="text-xs text-gray-400" data-testid="winback-stats">{w.sent} sent · {w.came_back} came back ({w.rate}%) · {fmt(w.revenue)} booked</p>
    </div>
    {w.items.length === 0 ? (
      <p className="text-sm text-gray-500" data-testid="winback-empty">No comeback texts yet. When a customer cancels, JARVIS suggests an offer on your daily call and tracks it here once you approve.</p>
    ) : (
      <div className="divide-y divide-white/5 text-sm">
        {w.items.slice(0, 12).map((x) => (
          <div key={x.winback_id} className="py-2 flex flex-wrap items-center gap-2" data-testid={`winback-${x.winback_id}`}>
            <span className="text-white min-w-[120px]">{x.name || x.phone}</span>
            {x.offer && <span className="text-[11px] px-2 rounded-full border border-sky-500/30 text-sky-300">{x.offer}</span>}
            <span className="text-gray-500 text-xs">sent {new Date(x.sent_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>
            <span className={`ml-auto text-xs ${x.came_back ? "text-emerald-400" : "text-gray-500"}`}>
              {x.came_back ? `Came back · ${x.bookings} booking${x.bookings === 1 ? "" : "s"} · ${fmt(x.booked_value)}${x.paid ? ` (${fmt(x.paid)} paid)` : ""}` : "Not back yet"}
            </span>
          </div>
        ))}
      </div>
    )}
  </div>
);

export const MoneyCards = () => {
  const [m, setM] = useState(null);
  const [w, setW] = useState(null);
  useEffect(() => {
    const load = () => { apiGet("/admin/money").then(setM).catch(() => {}); apiGet("/admin/winbacks").then(setW).catch(() => {}); };
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);
  if (!m) return null;
  return (
    <div className="space-y-3" data-testid="money-dashboard">
      <div className="grid md:grid-cols-3 gap-2 sm:gap-3">{PERIODS.map(([k, l], i) => <Period key={k} label={l} v={m[k]} i={i} />)}</div>
      <MoneyTrend />
      <PriceCoach />
      <SavingsGoals />
      {w && <WinBacks w={w} />}
    </div>
  );
};
